/* eslint-disable @typescript-eslint/no-use-before-define */
import { createContext, useContext } from 'react'
import type { PropsWithChildren } from 'react'

import { useToggleExclusionMutation } from '@Store/api/product'

import {
  BaseModal,
  type BaseModalDispatch,
  BaseModalProvider,
  type BaseModalState,
  useBaseModalDispatch,
  useBaseModalState,
} from './BaseModal'

interface ToggleExclusionState extends BaseModalState {}

interface ToggleExclusionDispatch extends BaseModalDispatch {
  toggleExclusion: (payload?: any) => void
}

const ToggleExclusionStateContext = createContext<ToggleExclusionState>(
  {} as ToggleExclusionState,
)
ToggleExclusionStateContext.displayName = 'ToggleExclusionState'

const ToggleExclusionDispatchContext = createContext<ToggleExclusionDispatch>(
  {} as ToggleExclusionDispatch,
)
ToggleExclusionDispatchContext.displayName = 'ToggleExclusionDispatch'

function ToggleExclusionProvider({
  children,
  modalContent,
}: PropsWithChildren & { modalContent?: JSX.Element }): JSX.Element {
  return (
    <BaseModalProvider>
      <ToggleExclusionProviderInner modalContent={modalContent}>
        {children}
      </ToggleExclusionProviderInner>
    </BaseModalProvider>
  )
}

function ToggleExclusionProviderInner({
  children,
  modalContent,
}: PropsWithChildren & { modalContent?: JSX.Element }): JSX.Element {
  const baseState = useBaseModalState()
  const baseDispatch = useBaseModalDispatch()

  const [toggleExclusion] = useToggleExclusionMutation()

  return (
    <ToggleExclusionStateContext.Provider value={baseState}>
      <ToggleExclusionDispatchContext.Provider
        value={{ ...baseDispatch, toggleExclusion }}
      >
        <BaseModal isOpen={baseState.isOpen} close={baseDispatch.close}>
          {modalContent}
        </BaseModal>
        {children}
      </ToggleExclusionDispatchContext.Provider>
    </ToggleExclusionStateContext.Provider>
  )
}

function useToggleExclusionState(): ToggleExclusionState {
  try {
    const context = useContext(ToggleExclusionStateContext)
    return context
  } catch (error) {
    throw new Error(
      'useToggleExclusionState must be used within a ToggleExclusionProvider',
    )
  }
}

function useToggleExclusionDispatch(): ToggleExclusionDispatch {
  try {
    const context = useContext(ToggleExclusionDispatchContext)
    return context
  } catch (error) {
    throw new Error(
      'useToggleExclusionDispatch must be used within a ToggleExclusionProvider',
    )
  }
}

export {
  ToggleExclusionProvider,
  useToggleExclusionState,
  useToggleExclusionDispatch,
}
